/**
 * PVI Graph Service — compiles Mist source to PVI graph.
 *
 * Mirrors the real rivellum-mist-compiler pipeline:
 *   Source → Parse → AST Value Ops → PVI Nodes + Edges
 *
 * The Rust compiler pipeline is:
 *   AST → VIR (SSA) → PVI (DAG)
 *
 * Since VIR is an SSA form used for optimization and the PVI lowering
 * simply walks entry-function value ops, we can go directly from
 * parsed AST value ops to PVI nodes, exactly as the real Rust lowerer does.
 *
 * This service also attaches source spans to each PVI node — something
 * the Rust compiler intentionally does NOT do (spans are not part of
 * the commitment). We add them for the IDE's bidirectional linking.
 */

import type {
  PviNode,
  PviEdge,
  PviNodeType,
  PviGraphWithSpans,
  PviNodeWithSpan,
  PviCompileResult,
  SourceSpan,
  PartyRef,
  NodeId,
  EdgeId,
  Condition,
  Operator,
} from './pviTypes';

/* ================================================================== */
/*  Parsed AST value op (extracted from source)                       */
/* ================================================================== */

interface ParsedValueOp {
  type: PviNodeType;
  /** vault/result name for Hold, Release, Refund, Split, Mint, Burn */
  vault: string | null;
  amount: string;
  asset: string;
  /** source party (from) */
  from: string | null;
  /** destination party (to) */
  to: string | null;
  /** mint authority */
  authority: string | null;
  /** split targets */
  splits: { percentBp: number; to: string }[];
  /** Source span for bidirectional linking */
  span: SourceSpan;
  /** Raw source text */
  sourceText: string;
  /** Conditions from preceding `require` statements */
  conditions: Condition[];
  /** Name of the enclosing function */
  functionName: string | null;
}

/* ================================================================== */
/*  Source → Value Ops parser                                          */
/* ================================================================== */

const REQUIRE_ORACLE_RE = /^\s*require\s+oracle\s*(?:\(\s*["']([^"']+)["']\s*\)|\.)\s*\.?\s*(\w+)\s*(>|>=|<|<=|==|!=)\s*([\d.]+)\s*$/i;
const REQUIRE_HEIGHT_RE = /^\s*require\s+(?:block_height|height)\s*(>|>=|<|<=|==|!=)\s*(\d+)\s*$/i;
const VALUE_OP_REGEX = /^(\s*)(?:(\w+)\s*=\s*)?(hold|pay|release|refund|split|mint|burn)\b(.*)$/i;
/**
 * Mist value op syntax (from real Mist grammar):
 *   hold <vault> = <amount> asset "<token>" from <sender>
 *   pay <amount> asset "<token>" to <recipient> [from <vault>]
 *   release vault <vault> to <recipient>
 *   refund vault <vault> to <recipient>
 *   split vault <vault> into [
 *     <percent>% to <recipient>
 *     ...
 *   ]
 *   mint <vault> = <amount> asset "<token>" authority <key>
 *   burn vault <vault>
 */
const HOLD_RE = /^(?:(\w+)\s*=\s*)?(\S+)\s+(?:of|asset)\s+(\S+)\s+from\s+(\S+)$/i;
const PAY_RE = /^(\S+)\s+(?:of|asset)\s+(\S+)\s+to\s+(\S+)(?:\s+from\s+(\S+))?$/i;
const RELEASE_RE = /^(?:vault\s+)?(\S+)\s+to\s+(\S+)$/i;
const REFUND_RE = /^(?:vault\s+)?(\S+)\s+to\s+(\S+)$/i;
const MINT_RE = /^(\S+)\s+(?:of|asset)\s+(\S+)\s+authority\s+(\S+)$/i;
const BURN_RE = /^(?:vault\s+)?(\S+)$/i;
const SPLIT_TARGET_RE = /^\s*(\d+)%\s+to\s+(\S+)\s*$/i;

function parseOperator(op: string): Operator {
  switch (op) {
    case '>': return 'gt';
    case '>=': return 'ge';
    case '<': return 'lt';
    case '<=': return 'le';
    case '==': return 'eq';
    case '!=': return 'ne';
    default: return 'gt';
  }
}

function extractValueOps(source: string): ParsedValueOp[] {
  const lines = source.split('\n');
  const ops: ParsedValueOp[] = [];
  const stripQuotes = (s: string) => s.replace(/^["']|["']$/g, '');

  // Accumulate conditions from `require` lines preceding a value op
  let pendingConditions: Condition[] = [];
  let currentFunction: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Reset pending conditions on function boundary and track function name
    const fnMatch = line.match(/^\s*function\s+(\w+)/);
    if (fnMatch) {
      pendingConditions = [];
      currentFunction = fnMatch[1];
      continue;
    }

    // Parse `require oracle("...").field op value`
    const oracleMatch = REQUIRE_ORACLE_RE.exec(line);
    if (oracleMatch) {
      pendingConditions.push({
        type: 'oracle_threshold',
        oracle_id: oracleMatch[1],
        field: oracleMatch[2],
        operator: parseOperator(oracleMatch[3]),
        value: parseFloat(oracleMatch[4]),
      });
      continue;
    }

    // Parse `require block_height op value`
    const heightMatch = REQUIRE_HEIGHT_RE.exec(line);
    if (heightMatch) {
      pendingConditions.push({
        type: 'time_absolute',
        after_height: parseInt(heightMatch[2], 10),
      });
      continue;
    }

    const m = VALUE_OP_REGEX.exec(line);
    if (!m) continue;

    const assignTarget = m[2] || null;
    const keyword = m[3].toLowerCase() as Lowercase<PviNodeType>;
    const rest = m[4].trim();
    const lineNum = i + 1;
    const col = (m[1]?.length ?? 0) + 1;

    const baseSpan: SourceSpan = {
      startLine: lineNum,
      startColumn: col,
      endLine: lineNum,
      endColumn: line.length + 1,
    };

    switch (keyword) {
      case 'hold': {
        const hm = HOLD_RE.exec(rest);
        if (hm) {
          ops.push({
            type: 'HOLD',
            vault: hm[1] || assignTarget,
            amount: hm[2],
            asset: stripQuotes(hm[3]),
            from: hm[4],
            to: null,
            authority: null,
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'pay': {
        const pm = PAY_RE.exec(rest);
        if (pm) {
          // If no explicit "from <vault>", the first arg is the vault ref
          const payVault = pm[4] || pm[1];
          ops.push({
            type: 'PAY',
            vault: payVault,
            amount: pm[1],
            asset: stripQuotes(pm[2]),
            from: payVault,
            to: pm[3],
            authority: null,
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'release': {
        const rm = RELEASE_RE.exec(rest);
        if (rm) {
          ops.push({
            type: 'RELEASE',
            vault: rm[1],
            amount: '0',
            asset: '',
            from: null,
            to: rm[2],
            authority: null,
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'refund': {
        const rfm = REFUND_RE.exec(rest);
        if (rfm) {
          ops.push({
            type: 'REFUND',
            vault: rfm[1],
            amount: '0',
            asset: '',
            from: null,
            to: rfm[2],
            authority: null,
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'split': {
        // Parse "split <vault> [" line, then collect targets
        const sm = /^(?:vault\s+)?(\S+)\s*(?:into\s*)?\[\s*$/i.exec(rest);
        if (sm) {
          const targets: { percentBp: number; to: string }[] = [];
          let endLine = lineNum;
          // Scan subsequent lines for split targets until ']'
          for (let j = i + 1; j < lines.length; j++) {
            const tl = lines[j].trim();
            if (tl === ']' || tl === '') {
              endLine = j + 1;
              if (tl === ']') break;
              continue;
            }
            const tm = SPLIT_TARGET_RE.exec(tl);
            if (tm) {
              targets.push({ percentBp: parseInt(tm[1], 10) * 100, to: tm[2] });
              endLine = j + 1;
            } else {
              break;
            }
          }
          ops.push({
            type: 'SPLIT',
            vault: sm[1],
            amount: '0',
            asset: '',
            from: null,
            to: null,
            authority: null,
            splits: targets,
            span: { ...baseSpan, endLine },
            sourceText: lines.slice(i, endLine).map(l => l.trim()).join(' '),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'mint': {
        const mm = MINT_RE.exec(rest);
        if (mm) {
          ops.push({
            type: 'MINT',
            vault: assignTarget,
            amount: mm[1],
            asset: stripQuotes(mm[2]),
            from: null,
            to: null,
            authority: mm[3],
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }

      case 'burn': {
        const bm = BURN_RE.exec(rest);
        if (bm) {
          ops.push({
            type: 'BURN',
            vault: bm[1],
            amount: '0',
            asset: '',
            from: null,
            to: null,
            authority: null,
            splits: [],
            span: baseSpan,
            sourceText: line.trim(),
            conditions: [...pendingConditions],
            functionName: currentFunction,
          });
          pendingConditions = [];
        }
        break;
      }
    }
  }

  return ops;
}

/* ================================================================== */
/*  Value Ops → PVI Graph (mirrors lower_vir_to_pvi in pvi.rs)        */
/* ================================================================== */

function buildPviGraph(ops: ParsedValueOp[]): PviGraphWithSpans {
  const nodes: PviNodeWithSpan[] = [];
  const edges: PviEdge[] = [];
  const vaultMap = new Map<string, NodeId>();

  /** Normalise vault identifiers so self.x matches x */
  const vaultKey = (name: string) => name.replace(/^self\./, '');
  let nextNodeId = 0;
  let nextEdgeId = 0;

  function makeParty(addr: string | null): PartyRef {
    return addr ? { type: 'account', address: addr } : { type: 'null' };
  }

  function addNode(
    nodeType: PviNodeType,
    op: ParsedValueOp,
    overrides?: Partial<PviNode>,
  ): NodeId {
    const id = nextNodeId++;
    nodes.push({
      id,
      nodeType,
      amount: overrides?.amount ?? op.amount,
      assetId: overrides?.assetId ?? op.asset,
      party: overrides?.party ?? makeParty(op.to ?? op.from),
      conditions: overrides?.conditions ?? op.conditions,
      mintAuthorityHash: null,
      label: overrides?.label ?? null,
      span: op.span,
      sourceText: op.sourceText,
      functionName: op.functionName,
    });
    return id;
  }

  function addEdge(source: NodeId, dest: NodeId, weightBp = 0): EdgeId {
    const id = nextEdgeId++;
    edges.push({
      id,
      source,
      destination: dest,
      amount: '0',
      conditions: [],
      choiceGroup: null,
      priority: 0,
      weightBp,
    });
    return id;
  }

  for (const op of ops) {
    switch (op.type) {
      case 'HOLD': {
        const nid = addNode('HOLD', op, { party: makeParty(op.from) });
        if (op.vault) vaultMap.set(vaultKey(op.vault), nid);
        break;
      }

      case 'PAY': {
        const nid = addNode('PAY', op, { party: makeParty(op.to) });
        const pk = op.from ? vaultKey(op.from) : null;
        if (pk && vaultMap.has(pk)) {
          addEdge(vaultMap.get(pk)!, nid);
        }
        break;
      }

      case 'RELEASE': {
        // Inherit asset from upstream HOLD node via vault reference
        const rk = op.vault ? vaultKey(op.vault) : null;
        const rkHoldId = rk ? vaultMap.get(rk) : undefined;
        const rkHoldNode = rkHoldId !== undefined ? nodes.find(n => n.id === rkHoldId) : undefined;
        const nid = addNode('RELEASE', op, {
          party: makeParty(op.to),
          assetId: rkHoldNode?.assetId || op.asset || undefined,
        });
        if (rkHoldId !== undefined) {
          addEdge(rkHoldId, nid);
        }
        break;
      }

      case 'REFUND': {
        // Inherit asset from upstream HOLD node via vault reference
        const rfk = op.vault ? vaultKey(op.vault) : null;
        const rfkHoldId = rfk ? vaultMap.get(rfk) : undefined;
        const rfkHoldNode = rfkHoldId !== undefined ? nodes.find(n => n.id === rfkHoldId) : undefined;
        const nid = addNode('REFUND', op, {
          party: makeParty(op.to),
          assetId: rfkHoldNode?.assetId || op.asset || undefined,
        });
        if (rfkHoldId !== undefined) {
          addEdge(rfkHoldId, nid);
        }
        break;
      }

      case 'SPLIT': {
        const splitNid = addNode('SPLIT', op, { party: { type: 'null' } });
        const sk = op.vault ? vaultKey(op.vault) : null;
        if (sk && vaultMap.has(sk)) {
          addEdge(vaultMap.get(sk)!, splitNid);
        }
        // Create PAY node per target
        for (const target of op.splits) {
          const payNid = addNode('PAY', op, {
            party: makeParty(target.to),
            label: `${target.percentBp / 100}% to ${target.to}`,
          });
          addEdge(splitNid, payNid, target.percentBp);
        }
        break;
      }

      case 'MINT': {
        const nid = addNode('MINT', op, {
          party: { type: 'null' },
          mintAuthorityHash: op.authority ?? null,
        } as Partial<PviNode>);
        if (op.vault) vaultMap.set(vaultKey(op.vault), nid);
        break;
      }

      case 'BURN': {
        const nid = addNode('BURN', op, { party: { type: 'null' } });
        const bk = op.vault ? vaultKey(op.vault) : null;
        if (bk && vaultMap.has(bk)) {
          addEdge(vaultMap.get(bk)!, nid);
        }
        break;
      }
    }
  }

  return {
    nodes,
    edges,
    metadata: { laneId: 0, specVersion: 1 },
  };
}

/* ================================================================== */
/*  Validation overlays                                               */
/* ================================================================== */

export interface GraphWarning {
  nodeId: NodeId;
  message: string;
  code: string;
}

function analyzeGraph(graph: PviGraphWithSpans): GraphWarning[] {
  const warnings: GraphWarning[] = [];

  // Check: all HOLD nodes should have outgoing edges (locked funds)
  for (const node of graph.nodes) {
    if (node.nodeType === 'HOLD') {
      const hasOutgoing = graph.edges.some(e => e.source === node.id);
      if (!hasOutgoing) {
        warnings.push({
          nodeId: node.id,
          message: 'Held value is never released, refunded, or paid — potential locked funds',
          code: 'MIST_E_LOCKED_FUNDS',
        });
      }
    }
  }

  // Check: SPLIT targets should have outgoing edges
  for (const node of graph.nodes) {
    if (node.nodeType === 'SPLIT') {
      const outEdges = graph.edges.filter(e => e.source === node.id);
      if (outEdges.length === 0) {
        warnings.push({
          nodeId: node.id,
          message: 'Split has no target destinations',
          code: 'MIST_E_SPLIT_SUM',
        });
      }
      // Sum of weight_bp should be 10000 (100%)
      const totalBp = outEdges.reduce((s, e) => s + e.weightBp, 0);
      if (outEdges.length > 0 && totalBp !== 10000) {
        warnings.push({
          nodeId: node.id,
          message: `Split percentages sum to ${totalBp / 100}%, expected 100%`,
          code: 'MIST_E_SPLIT_PERCENTAGE',
        });
      }
    }
  }

  // Check: MINT must have authority
  for (const node of graph.nodes) {
    if (node.nodeType === 'MINT' && !node.mintAuthorityHash) {
      warnings.push({
        nodeId: node.id,
        message: 'Mint operation missing authority reference',
        code: 'MIST_E_MINT_AUTHORITY',
      });
    }
  }

  // Check: unreachable nodes (no incoming edges, not HOLD/MINT source nodes)
  for (const node of graph.nodes) {
    if (node.nodeType === 'HOLD' || node.nodeType === 'MINT') continue;
    const hasIncoming = graph.edges.some(e => e.destination === node.id);
    if (!hasIncoming) {
      warnings.push({
        nodeId: node.id,
        message: `${node.nodeType} node has no incoming value flow — unreachable`,
        code: 'MIST_E_UNREACHABLE',
      });
    }
  }

  return warnings;
}

/* ================================================================== */
/*  Public API                                                        */
/* ================================================================== */

/**
 * Compile Mist source code to a PVI graph.
 *
 * Pipeline: Source → extractValueOps → buildPviGraph → analyzeGraph
 *
 * Returns null graph if:
 * - source is not a .mist file
 * - source is empty
 * - source has no contract declaration
 * - source has no value operations
 */
export function compileToPvi(source: string, fileName: string): PviCompileResult {
  const timestamp = Date.now();

  if (!fileName.endsWith('.mist')) {
    return {
      status: 'idle',
      graph: null,
      errors: [],
      warnings: [],
      timestamp,
      stale: false,
    };
  }

  const emptyGraph: PviGraphWithSpans = { nodes: [], edges: [], metadata: { laneId: 0, specVersion: 1 } };

  const trimmed = source.trim();
  if (!trimmed) {
    return {
      status: 'error',
      graph: emptyGraph,
      errors: ['Empty source file'],
      warnings: [],
      timestamp,
      stale: false,
    };
  }

  if (!/^\s*contract\s+\w+/m.test(trimmed)) {
    return {
      status: 'error',
      graph: emptyGraph,
      errors: ['No contract declaration found — PVI graphs are compiled from Mist contracts'],
      warnings: [],
      timestamp,
      stale: false,
    };
  }

  try {
    const ops = extractValueOps(source);

    if (ops.length === 0) {
      return {
        status: 'success',
        graph: { nodes: [], edges: [], metadata: { laneId: 0, specVersion: 1 } },
        errors: [],
        warnings: ['Contract has no value operations — the PVI graph is empty'],
        timestamp,
        stale: false,
      };
    }

    const graph = buildPviGraph(ops);
    const graphWarnings = analyzeGraph(graph);

    return {
      status: 'success',
      graph,
      errors: [],
      warnings: graphWarnings.map(w => `[${w.code}] ${w.message}`),
      timestamp,
      stale: false,
    };
  } catch (err) {
    // Best-effort: try to extract whatever ops are parseable
    let bestEffortGraph: PviGraphWithSpans = emptyGraph;
    try {
      const fallbackOps = extractValueOps(source);
      if (fallbackOps.length > 0) {
        bestEffortGraph = buildPviGraph(fallbackOps);
      }
    } catch { /* ignore — use empty graph */ }
    return {
      status: 'error',
      graph: bestEffortGraph,
      errors: [`Compile error: ${err instanceof Error ? err.message : String(err)}`],
      warnings: [],
      timestamp,
      stale: false,
    };
  }
}

/**
 * Get node-level warnings for graph overlay.
 */
export function getGraphWarnings(graph: PviGraphWithSpans): GraphWarning[] {
  return analyzeGraph(graph);
}

/**
 * Find the PVI node at a given source line.
 * Used for source → graph highlighting.
 */
export function findNodeAtLine(graph: PviGraphWithSpans, line: number): PviNodeWithSpan | null {
  return graph.nodes.find(n =>
    n.span && line >= n.span.startLine && line <= n.span.endLine,
  ) ?? null;
}

/**
 * Export PVI graph as the canonical JSON format matching .pvi.json output.
 */
export function exportPviJson(graph: PviGraphWithSpans): Record<string, unknown> {
  return {
    nodes: graph.nodes.map(n => ({
      id: n.id,
      node_type: n.nodeType,
      amount: n.amount,
      asset_id: n.assetId,
      party: n.party.type === 'null'
        ? { Null: null }
        : { Account: { id: n.party.address } },
      conditions: n.conditions,
      mint_authority_hash: n.mintAuthorityHash,
      metadata: { label: n.label },
    })),
    edges: graph.edges.map(e => ({
      id: e.id,
      source: e.source,
      destination: e.destination,
      amount: e.amount,
      conditions: e.conditions,
      choice_group: e.choiceGroup,
      priority: e.priority,
      weight_bp: e.weightBp,
    })),
    metadata: {
      lane_id: graph.metadata.laneId,
      oracle_snapshot_height: 0,
      oracle_snapshot_commitment: '0'.repeat(64),
      spec_version: graph.metadata.specVersion,
      capabilities: 0,
      asset_id_codec: 0,
      routing_epoch: 0,
    },
  };
}
